import { useState, useEffect, useMemo, useCallback } from 'react';
import { Row, Col, Input, Container } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { CatalogoAPI } from '../../../../shared/http-requests';
import { SpinnerConstructor, AppPagination } from '../../../../shared/components';
import classNames from 'classnames';
import defaultBackgroundImage from '../../../../../images/defaultBackgroundImage.png';
import GenericCarousel from '../../../../shared/components/GenericCarousel';
import { connect } from 'react-redux';
import { useAppContext } from '../../../../shared/contexts/AppContextProvider';
import Axios from 'axios';
import AppCover from '../../../../shared/components/AppCover';

const Secoes = ({ secoes = [], guiaSelecionada, ...props }) => {
    const [destaques, setDestaques] = useState([]);
    const [noticias, setNoticias] = useState([]);
    const [pagination, setPagination] = useState({});
    const [showDestaques, setShowDestaques] = useState(true);
    const [selectedOrderBy, setSelectedOrderBy] = useState('obra');
    const [selectedPerPage, setSelectedPerPage] = useState(7);
    const [loadingDestaques, setLoadingDestaques] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [showDestaquesMobile, setShowDestaquesMobile] = useState(false);

    const selectPerPage = [7, 14, 21, 28, 35, 42, 49];
    const { codAcervo } = useParams();
    const { lang } = useAppContext();
    const { t } = useTranslation();

    const {
        instituicaoRx: {
            capa_cor_fonte_padrao_classico,
            capa_padrao_classico,
        } = {
            capa_cor_fonte_padrao_classico: '#000000',
            capa_padrao_classico: defaultBackgroundImage,
            usa_capa_padrao_classico: 'N',
        },
    } = props;

    const selectOrderBy = useMemo(
        () => [
            { label: t('Titulo'), value: 'obra' },
            { label: t('AnoPublicacao'), value: 'ano_publicacao' },
            { label: t('CodigoAcervo'), value: 'cod_acervo' },
            { label: t('TipoObra'), value: 'cod_tipo_obra' },
            { label: t('Idioma'), value: 'idioma' },
            { label: t('DescricaoFisica'), value: 'desc_fisica' },
            { label: t('Classificacao'), value: 'classificacao' },
            { label: t('Referencia'), value: 'referencia' },
        ],
        [t]
    );

    const toggleDestaquesMobile = () => {
        setShowDestaquesMobile((prev) => !prev);
    };

    const getCodIdioma = useCallback(() => {
        switch (lang) {
            case "en":
                return 2; 
            case "es":
                return 3; 
            case "pt":
            default:
                return 1;
        }
    }, [lang]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };

        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const targetDiv = document.querySelector('.consultaContent');

        if (targetDiv) {
            targetDiv.scrollIntoView({ behavior: 'smooth' });
        }
    }, [codAcervo]);

    useEffect(() => {
        if (!isMobile) {
            setShowDestaquesMobile(false);
        }
    }, [isMobile]);

    useEffect(() => {
        const cancelTokenSrc = Axios.CancelToken.source();

        (async () => {
            const hasSection = Array.isArray(secoes)
                && secoes.some(
                    (x) => String(x?.tipo_secao || '').toLowerCase() === 'noticias'
                );

            if (!hasSection) {
                setNoticias([]);
                return;
            }

            try {
                const response = await CatalogoAPI.buscarNoticias(
                    guiaSelecionada,
                    'C',
                    cancelTokenSrc.token
                );

                if (Array.isArray(response?.data)) {
                    setNoticias(response.data);
                } else {
                    setNoticias([]);
                }
            } catch (e) {
                if (!Axios.isCancel(e)) {
                    console.log(e);
                    setNoticias([]);
                }
            }
        })();

        return () => {
            cancelTokenSrc.cancel();
        };
    }, [lang, guiaSelecionada, secoes]);

    const findDestaques = useCallback(
        async (
            {
                page = 1,
                perPage = selectedPerPage,
                orderBy = selectedOrderBy,
            } = {},
            cancelToken = null
        ) => {
            const hasDestaqueSection = Array.isArray(secoes)
                && secoes.some(
                    (x) => String(x?.tipo_secao || '').toLowerCase() === 'destaque'
                );

            if (!hasDestaqueSection) {
                setDestaques([]);
                setPagination({});
                setShowDestaques(false);
                setLoadingDestaques(false);
                return;
            }

            const cod_idioma = getCodIdioma();

            try {
                const response = await CatalogoAPI.buscarDestaques(
                    {
                        page,
                        perPage,
                        orderBy,
                        cod_idioma,
                    },
                    cancelToken
                );

                if (response?.data) {
                    const {
                        destaques: destaquesData = [],
                        pagination: paginationData = {},
                        showDestaques: showDestaquesResponse = true,
                    } = response.data;

                    setDestaques(Array.isArray(destaquesData) ? destaquesData : []);
                    setPagination(paginationData || {});
                    setShowDestaques(Boolean(showDestaquesResponse));
                } else {
                    setDestaques([]);
                    setPagination({});
                    setShowDestaques(false);
                }
            } catch (e) {
                if (!Axios.isCancel(e)) {
                    console.log(e);
                    setDestaques([]);
                    setPagination({});
                    setShowDestaques(false);
                }
            } finally {
                setLoadingDestaques(false);
            }
        },
        [secoes, selectedPerPage, selectedOrderBy, getCodIdioma]
    );

    useEffect(() => {
        const cancelTokenSrc = Axios.CancelToken.source();

        const hasDestaqueSection = Array.isArray(secoes)
            && secoes.some(
                (x) => String(x?.tipo_secao || '').toLowerCase() === 'destaque'
            );

        if (!hasDestaqueSection) {
            setDestaques([]);
            setPagination({});
            setShowDestaques(false);
            setLoadingDestaques(false);

            return () => {
                cancelTokenSrc.cancel();
            };
        }

        setLoadingDestaques(true);

        findDestaques({}, cancelTokenSrc.token);

        return () => {
            cancelTokenSrc.cancel();
        };
    }, [findDestaques, secoes, lang]);

    const agruparNoticias = (listaNoticias, quantidadePorSlide) => {
        if (!Array.isArray(listaNoticias) || quantidadePorSlide <= 0) {
            return [];
        }

        const grupos = [];

        for (let i = 0; i < listaNoticias.length; i += quantidadePorSlide) {
            grupos.push(listaNoticias.slice(i, i + quantidadePorSlide));
        }

        return grupos;
    };

    const noticiasAgrupadas = useMemo(() => {
        const itensPorSlide = isMobile ? 1 : 3;
        return agruparNoticias(noticias, itensPorSlide);
    }, [noticias, isMobile]);

    const renderizarDestaques = (secao) => {
        const temDestaques = Array.isArray(destaques) && destaques.length > 0;
        const exibirConteudoDestaques = !isMobile || showDestaquesMobile;

        if (!loadingDestaques && !temDestaques) {
            return <div />;
        }

        return (
            <div className="p-2">
                <div className="destaques bkbranco p-2">
                    <div className="bkbranco">
                        <Col md="12" className="col-12 py-3">
                            <Row>
                                <Col className="d-flex align-items-center">
                                    <h3 className="s24 cortxt mb-0 inline">
                                        {secao?.nome_secao}
                                    </h3>
                                </Col>

                                <div className="d-md-none bkbranco">
                                    <label
                                        onClick={toggleDestaquesMobile}
                                        className="l20 f500 branco hover hpreto bklaranja px-2 py-1 rad5 click slaranja"
                                    >
                                        {showDestaquesMobile ? t('Ocultar') : t('Mostrar')}
                                    </label>
                                </div>
                            </Row>
                        </Col>

                        <div
                            className={classNames({
                                'd-none': !exibirConteudoDestaques,
                                'd-block': exibirConteudoDestaques,
                            })}
                        >
                            <Row
                                className={classNames('container m-auto px-md-0 pb-4', {
                                    'd-flex justify-content-end': (pagination?.lastPage || 0) <= 1,
                                })}
                            >
                                {(pagination?.lastPage || 0) > 1 ? (
                                    <Col className="d-flex justify-content-start flex align-items-end px-md-0">
                                        <AppPagination
                                            activePage={pagination?.currentPage}
                                            itemsPerPage={pagination?.perPage}
                                            totalItems={pagination?.totalItems}
                                            onPageChange={(page) => {
                                                if (page === pagination?.currentPage) {
                                                    return;
                                                }

                                                setLoadingDestaques(true);
                                                findDestaques({ page });
                                            }}
                                        />
                                    </Col>
                                ) : null}

                                <Col md="3" lg="2">
                                    <div>
                                        <div>
                                            <span className="s13 cortxt f500">
                                                {t('OrdenarPor') || 'Ordenar por:'}
                                            </span>
                                        </div>

                                        <Input
                                            name="ordenacao"
                                            type="select"
                                            value={selectedOrderBy}
                                            onChange={(e) => {
                                                const { value } = e.currentTarget;
                                                setSelectedOrderBy(value);
                                                setLoadingDestaques(true);
                                                findDestaques({ orderBy: value, page: 1 });
                                            }}
                                        >
                                            {selectOrderBy.map((item) => (
                                                <option
                                                    key={`top_order-option_${item.value}`}
                                                    value={item.value}
                                                >
                                                    {item.label}
                                                </option>
                                            ))}
                                        </Input>
                                    </div>
                                </Col>

                                <Col className="px-md-0 items-per-page" md="3" lg="2">
                                    <div>
                                        <span className="s13 cortxt f500">
                                            {t('ItensPorPagina') || 'Itens por página:'}
                                        </span>
                                    </div>

                                    <Input
                                        type="select"
                                        name="quantPerPag"
                                        value={selectedPerPage}
                                        onChange={(e) => {
                                            const value = Number(e.currentTarget.value);
                                            setSelectedPerPage(value);
                                            setLoadingDestaques(true);
                                            findDestaques({ perPage: value, page: 1 });
                                        }}
                                    >
                                        {selectPerPage.map((item) => (
                                            <option
                                                key={`top_per_page_option_${item}`}
                                                value={item}
                                            >
                                                {item}
                                            </option>
                                        ))}
                                    </Input>
                                </Col>
                            </Row>

                            <div className="novasAquisicoesTipoLista py-2">
                                <Row>
                                    <ul className="__coverList container m-auto p-0">
                                        {loadingDestaques ? (
                                            <div className="p-5 text-center animated fadeIn">
                                                <SpinnerConstructor
                                                    visible
                                                    showLabel
                                                    height={80}
                                                    width={80}
                                                    className="apergamum"
                                                />
                                            </div>
                                        ) : (
                                            destaques.map((destaque, index) => (
                                                <div
                                                    key={`destaque_${destaque?.cod_acervo || index}`}
                                                    style={{ width: '150px' }}
                                                >
                                                    <AppCover
                                                        isbn={destaque?.isbn_issn}
                                                        codAcervo={destaque?.cod_acervo}
                                                        width="100%"
                                                        height="100%"
                                                        title={destaque?.obra}
                                                        link={`/acervo/${destaque?.cod_acervo}`}
                                                        src={destaque?.link_capa}
                                                        textColor={capa_cor_fonte_padrao_classico}
                                                        altSrc={capa_padrao_classico}
                                                    />
                                                </div>
                                            ))
                                        )}
                                    </ul>
                                </Row>
                            </div>

                            {(pagination?.perPage || 0) > 7 && (pagination?.totalItems || 0) > 7 ? (
                                <Row
                                    className={classNames('container m-auto px-md-0', {
                                        'd-flex justify-content-end': (pagination?.lastPage || 0) <= 1,
                                    })}
                                >
                                    {(pagination?.lastPage || 0) > 1 ? (
                                        <Col className="d-flex justify-content-start flex align-items-end px-md-0">
                                            <AppPagination
                                                activePage={pagination?.currentPage}
                                                itemsPerPage={pagination?.perPage}
                                                totalItems={pagination?.totalItems}
                                                onPageChange={(page) => {
                                                    if (page === pagination?.currentPage) {
                                                        return;
                                                    }

                                                    setLoadingDestaques(true);
                                                    findDestaques({ page });
                                                }}
                                            />
                                        </Col>
                                    ) : null}

                                    <Col md="3" lg="2">
                                        <div>
                                            <div>
                                                <span className="s13 cortxt f500">
                                                    {t('OrdenarPor') || 'Ordenar por:'}
                                                </span>
                                            </div>

                                            <Input
                                                name="ordenacao"
                                                type="select"
                                                value={selectedOrderBy}
                                                onChange={(e) => {
                                                    const { value } = e.currentTarget;
                                                    setSelectedOrderBy(value);
                                                    setLoadingDestaques(true);
                                                    findDestaques({ orderBy: value, page: 1 });
                                                }}
                                            >
                                                {selectOrderBy.map((item) => (
                                                    <option
                                                        key={`bottom_order-option_${item.value}`}
                                                        value={item.value}
                                                    >
                                                        {item.label}
                                                    </option>
                                                ))}
                                            </Input>
                                        </div>
                                    </Col>

                                    <Col className="px-md-0 items-per-page" md="2" lg="2">
                                        <div>
                                            <span className="s13 cortxt f500">
                                                {t('ItensPorPagina') || 'Itens por página:'}
                                            </span>
                                        </div>

                                        <Input
                                            type="select"
                                            name="quantPerPag"
                                            value={selectedPerPage}
                                            onChange={(e) => {
                                                const value = Number(e.currentTarget.value);
                                                setSelectedPerPage(value);
                                                setLoadingDestaques(true);
                                                findDestaques({ perPage: value, page: 1 });
                                            }}
                                        >
                                            {selectPerPage.map((item) => (
                                                <option
                                                    key={`bottom_per_page_option_${item}`}
                                                    value={item}
                                                >
                                                    {item}
                                                </option>
                                            ))}
                                        </Input>
                                    </Col>
                                </Row>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderNews = (noticia) => {
        const hasLink = noticia?.url !== null && noticia?.url?.trim() !== '';

        return (
            <div
                style={{ backgroundColor: 'rgba(var(--branco), .2)' }}
                className="img-fluid imgCarousel d-inline-block w-100"
            >
                {noticia?.nom_imagem ? (
                    <div className="text-center rad10">
                        <img
                            src={noticia.nom_imagem}
                            alt={noticia.titulo || 'Notícia'}
                            className="img-fluid rad10"
                        />
                    </div>
                ) : null}

                <div className="preto f300 s20 p-3">
                    <div
                        className={classNames('preto f500 p-1 rad10 s20', {
                            click: hasLink,
                        })}
                    >
                        {noticia?.titulo}
                    </div>

                    <div
                        className="s13 preto op70 f400 d-block text-wrap overflow-auto"
                        style={noticia?.nom_imagem ? { height: 85 } : {}}
                    >
                        {noticia?.conteudo}
                    </div>
                </div>
            </div>
        );
    };

    const getUrl = (url) => {
        if (!url || typeof url !== 'string' || url.trim() === '') {
            return '#';
        }

        const trimmedUrl = url.trim();
        const pattern = /^https?:\/\//i;

        if (!pattern.test(trimmedUrl)) {
            return `http://${trimmedUrl}`;
        }

        return trimmedUrl;
    };

    const renderizarNoticias = (secao) => {
        const totalItens = noticiasAgrupadas.length;
        const useJustifyCenter = totalItens < 3;

        if (!noticiasAgrupadas.length) {
            return null;
        }

        return (
            <div>
                <div className="bktema10 p-2">
                    <div className="container">
                        <h3 className="s24 cortxt mb-0 inline">{secao?.nome_secao}</h3>
                    </div>

                    <Container className="pb-5 p-1">
                        <GenericCarousel className="carouselNoticias">
                            {noticiasAgrupadas.map((grupo, index) => (
                                <Row
                                    key={`grupo_noticias_${index}`}
                                    className={
                                        useJustifyCenter
                                            ? 'justify-content-center w-100 m-0'
                                            : 'w-100 m-0'
                                    }
                                >
                                    {grupo.map((noticia) => (
                                        <Col
                                            key={noticia?.id_noticia}
                                            xs={12}
                                            md={4}
                                            className="mb-3 mb-md-0"
                                        >
                                            {noticia?.url ? (
                                                <a
                                                    href={getUrl(noticia.url)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {renderNews(noticia)}
                                                </a>
                                            ) : (
                                                renderNews(noticia)
                                            )}
                                        </Col>
                                    ))}
                                </Row>
                            ))}
                        </GenericCarousel>
                    </Container>
                </div>
            </div>
        );
    };

    if (Array.isArray(secoes) && secoes.length) {
        return secoes.map((secao, index) => {
            const tipoSecao = String(secao?.tipo_secao || '').toUpperCase();

            switch (tipoSecao) {
                case 'DESTAQUE':
                    return showDestaques ? (
                        <div key={`${secao?.tipo_secao}_${index}`}>
                            {renderizarDestaques(secao)}
                        </div>
                    ) : null;

                case 'NOTICIAS':
                    return (
                        <div key={`${secao?.tipo_secao}_${index}`}>
                            {renderizarNoticias(secao)}
                        </div>
                    );

                default:
                    return null;
            }
        });
    }

    return <div />;
};

function mapStateToProps(state) {
    return {
        instituicaoRx: state?.instituicao?.data,
    };
}

export default connect(mapStateToProps)(Secoes);